import { themeColors } from "../../../themeColors";
/**
 * @file   src\components\AgentDashboard\InventoryListing.js
 * @brief  This file is responsible for Inventory listing in agent dashboard section
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useMemo } from "react";
import _ from "lodash";
import useIsMobile from "../../../hooks/useIsMobile";
import MobileActionModal from "./MobileActionModal";
import {
  formateDate,
  loadPropertTypeImage,
  thousandSeperatedValue,
} from "../../../helpers/common";
import { goToFeedMatchTab } from "../../../helpers/matchTabRedirect";
import ActionDropdown from "../ActionDropdown";
import TableMoreIconVertical from "../../../images/table-more-vertical-icon.svg";
import EditIcon from "../../../images/dpdown-edit-client-icon.svg";
import TrashIcon from "../../../images/dpdown-trash-icon.svg";
import noImage from "../../../images/no-image-placeholder.png";
import { GenericTable, Paginator } from "@/components";
import TableHeader from "../TableHeader";
import { pageURLs } from "../../../constants/pageURLs";
import { useNavigate } from "react-router-dom";
import { formatInventoryAddress } from "@/helpers";
import { emptyPropertyPlaceholderImages } from "@/components/Onboarding/Dashboard/v2/PropertyInventoryListing";

const MLS_STATUS_LABEL = {
  Active: "Active",
  ActiveUnderContract: "Under Contract",
  Pending: "Pending",
  Closed: "Closed",
  Sold: "Sold",
  Expired: "Expired",
  Withdrawn: "Withdrawn",
};

const MLS_PILL_BG = "#FFF1E6";
const MLS_PILL_FG = "#D97706";

const MlsPriceIcon = ({ width = 18, height = 18 }) => (
  <svg width={width} height={height} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.306 7.4375L7.875 2.00649C7.79405 1.92488 7.69768 1.86019 7.5915 1.81615C7.48531 1.77212 7.37144 1.74964 7.25649 1.75H2.1875C2.07147 1.75 1.96019 1.7961 1.87815 1.87815C1.7961 1.96019 1.75 2.07147 1.75 2.1875V7.25649C1.74964 7.37144 1.77212 7.48531 1.81615 7.5915C1.86019 7.69768 1.92488 7.79405 2.00649 7.875L7.4375 13.306C7.51876 13.3873 7.61523 13.4518 7.7214 13.4957C7.82757 13.5397 7.94137 13.5624 8.05629 13.5624C8.17122 13.5624 8.28502 13.5397 8.39119 13.4957C8.49736 13.4518 8.59383 13.3873 8.67508 13.306L13.306 8.67508C13.3873 8.59383 13.4518 8.49736 13.4957 8.39119C13.5397 8.28502 13.5624 8.17122 13.5624 8.05629C13.5624 7.94137 13.5397 7.82757 13.4957 7.7214C13.4518 7.61523 13.3873 7.51876 13.306 7.4375ZM8.05602 12.6875L2.625 7.25649V2.625H7.25649L12.6875 8.05602L8.05602 12.6875ZM5.25 4.59375C5.25 4.72355 5.21152 4.85043 5.13941 4.95835C5.0673 5.06627 4.9648 5.15038 4.84489 5.20005C4.72498 5.24972 4.59303 5.26272 4.46573 5.23739C4.33843 5.21207 4.22149 5.14957 4.12972 5.05779C4.03794 4.96602 3.97544 4.84908 3.95011 4.72178C3.92479 4.59448 3.93779 4.46253 3.98746 4.34262C4.03713 4.2227 4.12124 4.12021 4.22916 4.0481C4.33708 3.97599 4.46396 3.9375 4.59375 3.9375C4.7678 3.9375 4.93472 4.00664 5.05779 4.12972C5.18086 4.25279 5.25 4.41971 5.25 4.59375Z" fill={MLS_PILL_FG}/>
  </svg>
);

const InventoryListing = (props) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [actionModal, setActionModal] = useState({ show: false, itemId: null });
  const {
    showNoResult,
    handleSort,
    itemList,
    onItemEdit,
    onItemDelete,
    sort,
    fromInvestorTab,
    emptyItemHtml,
    loading,
    currentPage,
    hasNextPage,
    onNextPage,
    onPrevPage,
    pageSize,
    onPageSizeChange,
    mode = "inventory",
  } = props;

  const handleInventoryItemClick = (inventoryId, tabKey = null, isPublished) => {
    if (!isPublished) {
      return onItemEdit(inventoryId);
    }

    const url = tabKey
      ? `${pageURLs.inventoryDetails}/${inventoryId}?tabKey=${tabKey}`
      : `${pageURLs.inventoryDetails}/${inventoryId}`;
    navigate(url, { state: { isFromAgentDashboard: true } });
  };

  const columns = useMemo(() => {
    const isMlsMode = mode === "mls";

    const dateColumn = {
      label: "Date",
      sortKey: "date",
      sorting: "Yes",
      cell: (item) => {
        const isMls = item?.rowType === "mls";
        const dateField = isMls
          ? _.get(item, "PublishedOn", "")
          : _.get(item, "CreatedOn", "");
        return formateDate(dateField, "shortMonth");
      },
    };

    const addressColumn = {
      label: "Address",
      sortKey: "address",
      sorting: isMlsMode ? "No" : "Yes",
      cell: (item) => {
        const isMls = item?.rowType === "mls";
        const photo = _.get(item, "photo", null);
        const location = _.get(item, "Location", "");

        if (!location) return null;

        let displayImage;
        if (isMls) {
          displayImage = photo || noImage;
        } else {
          const emptyPlaceholderImage = emptyPropertyPlaceholderImages[Math.floor(Math.random() * emptyPropertyPlaceholderImages.length)];
          const emptyPlaceholderImageUrl = require(`@/images/${emptyPlaceholderImage}`);
          displayImage = photo || emptyPlaceholderImageUrl;
        }

        const displayLocation = isMls ? location : formatInventoryAddress(location);

        return (
          <a
            href={void 0}
            role="button"
            className="txt-no-underline client-name-style"
          >
            <div className="d-flex align-items-center justify-content-start">
              <div className="table-address-img">
                <img
                  src={displayImage}
                  alt={isMls ? "mlsListingImage" : "inventoryImage"}
                  onError={(e) => { e.currentTarget.src = noImage; }}
                />
              </div>
              <span className="d-inline-block client-name-style txt-overflow-style">
                {displayLocation}
              </span>
            </div>
          </a>
        );
      },
    };

    const potentialBuyersColumn = {
      label: "Potential Buyers",
      sortKey: "potentialbuyers",
      sorting: "Yes",
      cell: (item) => {
        const isMls = item?.rowType === "mls";
        const potentialBuyers = _.get(item, "potentialBuyers", 0);
        const hasPotentialBuyers = potentialBuyers > 0;

        if (isMls) {
          const listingKey = _.get(item, "ListingKey", "");
          const handleClick = (e) => {
            e.stopPropagation();
            if (!hasPotentialBuyers || !listingKey) return;
            navigate(`/mls-listing/${encodeURIComponent(listingKey)}/matches`);
          };
          return (
            <div
              onClick={handleClick}
              className={`want-ad-tag medium-orange-tag-bg-border${hasPotentialBuyers ? " medium-orange-tag-bg-border-clickable" : ""} mb-0`}
            >
              {thousandSeperatedValue(potentialBuyers) || 0} Matches
            </div>
          );
        }

        const invId = _.get(item, "Inv_Id", "");
        const representingAsSeller = _.get(item, "Represent_as_Seller", "");
        const color = representingAsSeller ? "green" : "blue";

        const handleNavigateToMatches = (e) => {
          e.stopPropagation();
          if (!hasPotentialBuyers) return;
          goToFeedMatchTab(navigate, invId);
        };

        return (
          <div
            onClick={handleNavigateToMatches}
            className={`want-ad-tag medium-${color}-tag-bg-border${hasPotentialBuyers ? ` medium-${color}-tag-bg-border-clickable` : ""} mb-0`}
          >
            {thousandSeperatedValue(potentialBuyers) || 0} Matches
          </div>
        );
      },
    };

    const priceColumn = {
      label: "Price",
      sortKey: "price",
      sorting: "Yes",
      cell: (item) => {
        const isMls = item?.rowType === "mls";
        const price = _.get(item, "AskingPrice_Sell", 0);
        const n = Number(price);
        if (isMls && (!Number.isFinite(n) || n <= 0)) {
          return <p className="mb-0">—</p>;
        }
        return (
          <p className="mb-0">
            {n.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </p>
        );
      },
    };

    const propertyStatusColumn = {
      label: "Property Status",
      sortKey: "property_status",
      sorting: isMlsMode ? "No" : "Yes",
      cell: (item) => {
        const isMls = item?.rowType === "mls";
        if (isMls) {
          const std = _.get(item, "StandardStatus", "");
          const label = MLS_STATUS_LABEL[std] || std || "—";
          return <p style={{ marginBottom: 0 }}>{label}</p>;
        }
        const isDraft = _.get(item, "IsSavedDraft", "") === 1;
        const isSold = _.get(item, "IsSold", "") === 1;

        let status = "Active";
        if (isDraft) status = "Draft";
        else if (isSold) status = "Sold";

        return <p style={{ marginBottom: 0 }}>{status}</p>;
      },
    };

    const typeColumn = {
      label: "Type",
      sortKey: "type",
      sorting: isMlsMode ? "No" : "Yes",
      cell: (item) => {
        const isMls = item?.rowType === "mls";
        if (isMls) {
          return (
            <div
              className="d-flex align-items-center justify-content-start"
              style={{
                backgroundColor: MLS_PILL_BG,
                width: "fit-content",
                paddingRight: "5px",
                paddingLeft: "5px",
              }}
            >
              <div
                className="property-location-icon"
                style={{ background: "transparent", width: "auto", height: "auto" }}
              >
                <MlsPriceIcon />
              </div>
              <span style={{ color: MLS_PILL_FG, fontWeight: 500 }}>
                MLS Listing
              </span>
            </div>
          );
        }
        const representingAsSeller = _.get(item, "Represent_as_Seller", "");
        return (
          <div
            className="d-flex align-items-center justify-content-start"
            style={{
              backgroundColor: `${representingAsSeller ? themeColors.accentf100 : themeColors.secondary120}`,
              width: "fit-content",
              paddingRight: "5px",
              paddingLeft: "5px",
            }}
          >
            <div
              className="property-location-icon"
              style={{ background: "transparent", width: "auto", height: "auto" }}
            >
              {loadPropertTypeImage(representingAsSeller)}
            </div>
            <span
              style={{
                color: `${representingAsSeller ? themeColors.accentf600 : themeColors.secondary600}`,
                fontWeight: 500,
              }}
            >
              {representingAsSeller ? "POTENTIAL SELLER" : "SELLER"}
            </span>
          </div>
        );
      },
    };

    const clientColumn = {
      label: "Client",
      sorting: "Yes",
      sortKey: "client",
      cell: (item) => {
        if (fromInvestorTab) return null;
        const firstName = _.get(item, "FirstName", null);
        const lastName = _.get(item, "LastName", null);
        const isAgent = _.get(item, "Represent_as_Seller", "") == 1;
        const invId = _.get(item, "Inv_Id", "");

        const clientName =
          firstName || lastName
            ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
            : "";

        const client = clientName || (isAgent ? "Agent" : "");

        return (
          <div className="agent-want-listing-dropdown" onClick={(e) => e.stopPropagation()}>
            <p className="mb-0">{client}</p>
            <div className="social-btns-wrap no-bg-toggle ms-1">
              {isMobile ? (
                <img
                  src={TableMoreIconVertical}
                  alt="actions"
                  role="button"
                  onClick={() => setActionModal({ show: true, itemId: invId })}
                />
              ) : (
                <ActionDropdown
                  onItemEdit={onItemEdit}
                  onItemDelete={onItemDelete}
                  editText="Edit"
                  deleteText="Delete"
                  itemId={invId}
                  buyerListLinkText="BuyerListing Form"
                />
              )}
            </div>
          </div>
        );
      },
    };

    const base = [
      dateColumn,
      addressColumn,
      potentialBuyersColumn,
      priceColumn,
      propertyStatusColumn,
      typeColumn,
    ];
    return isMlsMode ? base : [...base, clientColumn];
  }, [mode, fromInvestorTab, isMobile, navigate, onItemEdit, onItemDelete]);

  return (
    <div className="dashboard-table-wrap">
      <div className="table-responsive wantad-table-responsive">
        {showNoResult && itemList !== null && itemList?.length > 0 ? (
          <GenericTable
            data={itemList}
            columns={columns}
            className="table"
            renderHeader={() => (
              <TableHeader
                columns={columns}
                handleSort={handleSort}
                sort={sort}
              />
            )}
            renderRow={(item) => {
              const isMls = item?.rowType === "mls";
              const onRowClick = isMls
                ? () => {
                    const key = _.get(item, "ListingKey", "");
                    if (key) navigate(`/mls-listing/${encodeURIComponent(key)}`);
                  }
                : () => handleInventoryItemClick(item.Inv_Id, null, item.IsPublished);
              return columns.map((col, idx) => (
                <td key={idx} style={{ cursor: 'pointer' }} onClick={onRowClick}>
                  {col.cell(item)}
                </td>
              ));
            }}
          />
        ) : (
          !loading &&
          showNoResult &&
          (itemList == null || itemList?.length == 0) &&
          (emptyItemHtml ? (
            emptyItemHtml()
          ) : (
            <div className="no-result-wrap no-result-padding">
              <h3>No results found</h3>
              <p>
                We couldn't find any listings that match your filters and search
                terms. Try a different search or reset all filters.
              </p>
            </div>
          ))
        )}
      </div>

      {showNoResult && itemList !== null && itemList?.length > 0 && (
        <Paginator
          currentPage={currentPage}
          hasNextPage={hasNextPage}
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      <MobileActionModal
        show={actionModal.show}
        onHide={() => setActionModal({ show: false, itemId: null })}
        actions={[
          { label: "Edit", icon: EditIcon, onClick: () => onItemEdit(actionModal.itemId) },
          { label: "Delete", icon: TrashIcon, color: "#dc3545", onClick: () => onItemDelete(actionModal.itemId) },
        ]}
      />
    </div>
  );
};
export default InventoryListing;
